import axios from "axios";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { SERVER_URL } from "../../constant";
import { CircularProgress } from "@mui/material";
import NotFound from "../../assets/not-found.png";
import AlertBox from "../../component/AlertBox";
import ButtonCompo from "../../component/ButtonCompo";
import DialogBox from "../../component/DialogBox";
import InputField from "../../component/InputField";

function SingleCompOffRecord() {
  const isEmployee = useSelector(
    (state) => state.employee?.isEmployeeAuthenticated
  );
  const employeeData = useSelector((state) => state.employee?.employeeData);

  const navigate = useNavigate();

  //redirect to signin if not logged in
  useEffect(() => {
    if (isEmployee !== undefined && isEmployee === false) {
      navigate("/employee/signin");
    }
  }, [isEmployee]);

  //redirect to employee dashboard if permission is not given
  useEffect(() => {
    if (employeeData !== null && employeeData?.adminRole !== "manager") {
      navigate("/employee/dashboard");
    }
  }, [employeeData]);

  /* ----------------------------------------------------------------------------------------------------- */
  /* ----------------------------------------Main Coding Part--------------------------------------------- */
  /* ----------------------------------------------------------------------------------------------------- */

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [recordData, setRecordData] = useState({});
  const [somethingError, setSomethingError] = useState(false);
  const [reload, setReload] = useState(false);
  const { recordId } = useParams();
  const [cancelReason, setCancelReason] = useState("");

  async function getRecordData() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/get/single/comp-off/record/${recordId}`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data);
      setRecordData(res.data?.data[0] || undefined);
    } catch (error) {
      console.log(error);
      setSomethingError(true);
    } finally {
      setLoading(false);
    }
  }

  async function withdrawCompOff() {
    if (cancelReason.trim() === "") {
      setOpen(true);
      setSuccess(false);
      setMessage("Please specify the cancel reason");
      return null;
    }
    const data = {
      recordId: recordId,
      cancellationNote: cancelReason,
    };
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/revoke/comp-off/manager`,
        method: "post",
        withCredentials: true,
        data: data,
      });
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(true);
      setMessage("Comp Off has been deducted");
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Withdraw failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getRecordData();
  }, [recordId, reload]);

  if (somethingError || !recordData) {
    return (
      <div className="mt-[5rem] flex flex-col justify-center items-center w-full">
        <img src={NotFound} />
        <p className="w-full text-center text-slate-500 font-medium text-[18px]">
          Comp Off record not found
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-[90vh] flex justify-center items-center">
        <CircularProgress size={"60px"} />
      </div>
    );
  }

  return (
    <>
      <div className="w-full flex justify-center items-center">
        {success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        {!success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        <div className="w-[30rem] py">
          <div className="w-[95vw] md:w-[30rem] p-5 border mt-5 rounded-[10px] shadow-md">
            <h1 className="text-center text-[19px] font-medium">
              Comp OFF Record
            </h1>
            <div className="mt-7 w-full space-y-2">
              <section className="grid grid-cols-2">
                <p className="font-medium">Applicant</p>
                <p className="text-slate-700">{recordData?.user?.fullName}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Role</p>
                <p className="text-slate-700">{recordData?.user?.role}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Leave Name</p>
                <p className="text-slate-700">{recordData?.leaveName}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Login Time</p>
                <p className="text-slate-700">{recordData?.loginTime}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Logout Time</p>
                <p className="text-slate-700">{recordData?.logoutTime}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Total Working Hours</p>
                <p className="text-slate-700">
                  {parseFloat(
                    (Number(recordData?.totalWorikingHours) / 60).toFixed(2)
                  )}{" "}
                  hours
                </p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Date</p>
                <p className="text-slate-700">
                  {new Date(recordData?.leaveDate).toLocaleDateString("en-IN")}
                </p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Total Days</p>
                <p className="text-slate-700">{recordData?.totalDay}</p>
              </section>
              <section className="grid grid-cols-2">
                <p className="font-medium">Cancel Status</p>
                <p className="text-slate-700">
                  {recordData?.isCanceled ? "Canceled" : "Not Canceled"}
                </p>
              </section>
              {recordData?.cancelDate && (
                <section className="grid grid-cols-2">
                  <p className="font-medium">Status</p>
                  <p className="text-slate-700">
                    {new Date(recordData?.cancelDate).toLocaleDateString(
                      "en-IN"
                    )}
                  </p>
                </section>
              )}
              {recordData?.cancelReason && (
                <section className="grid grid-cols-2">
                  <p className="font-medium">Why it was canceled</p>
                  <p className="text-slate-700">{recordData?.cancelReason}</p>
                </section>
              )}
              {recordData?.isCanceled === false && (
                <div className="flex justify-center items-center !mt-5">
                  <DialogBox
                    trigger={
                      <ButtonCompo variant={"contained"} color={"error"}>
                        Withdraw Comp Off
                      </ButtonCompo>
                    }
                    closeActionButton="Cancel"
                    actionButton={
                      <ButtonCompo color={"error"} onClick={withdrawCompOff}>
                        Withdraw
                      </ButtonCompo>
                    }
                  >
                    <div>
                      <p className="text-slate-700">
                        If you withdraw the comp off, the total days will be
                        deducted from the employee's leave account. Are you
                        still want to withdraw?
                      </p>
                      <section className="mt-5">
                        <InputField
                          label="Why are you withdrawing the comp off?"
                          onChange={(eve) =>
                            setCancelReason(eve?.target?.value)
                          }
                          required
                        />
                      </section>
                    </div>
                  </DialogBox>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SingleCompOffRecord;
